import { MobileModal } from 'components/UI/Modal/MobileModal'
import { useState, useEffect, useMemo } from 'react'
import useApiService from 'services/api'
import SearchPage from '../mobile/search'
import {
  ContactCompanySearchResult,
  CompanyTypeSlug,
  DoorSearchResult,
  TableTypeSlug,
} from 'models/companies'
import { ProductPrediction, DealCreate } from 'models/deals'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import MobileDealDetailsStep from 'components/Deals/CreateDealModal/MobileDealDetailsStep'
import FbButton from 'components/FbUI/FbButton'
import { RadioIconSelect } from 'components/FormUtils/RadioIconSelect'
import { Building2, MapPin } from 'lucide-react'
import { MobileSelectLocationsModal } from 'components/Deals/MobileEditDealModal/MobileSelectLocationsModal'
import { MobileSelectProductVariantModal } from 'components/Deals/MobileEditDealModal/MobileSelectProductVariantModal'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { DEAL_TABLE_KEY } from 'constants/tableQueryKeys'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FbLink } from 'components/FbUI/FbLink'
import {
  tryGetTargetType,
  UnifiedTargetType,
} from 'services/opportunities/targetTypeMap'

// Simplified schema for mobile - no door selection needed
const dealsFormSchema = z.object({
  deals: z.array(
    z.object({
      create: z.boolean(),
      product: z.number(),
      product_variant: z.number().optional().nullable(),
      monthly_volume_override: z.number().min(0).optional().nullable(),
      monthly_revenue_override: z.number().min(0).optional().nullable(),
      close_date_override: z.string().optional().nullable(),
      account_owner: z.number().optional().nullable(),
      sales_stage: z.number().optional().nullable(),
      origin_campaign_id: z.number().optional().nullable(),
      name: z
        .union([
          z.string().max(100, 'Deal name must be 100 characters or less'),
          z.null(),
          z.undefined(),
        ])
        .optional()
        .nullable(),
    })
  ),
  // Add these fields to match CreateDealModal's DealsFormValues type
  apply_to: z
    .union([z.literal('entire_company'), z.literal('select_locations')])
    .default('entire_company'),
  door_ids: z.array(z.number()).optional(),
})

export type DealsFormValues = z.infer<typeof dealsFormSchema>

type SelectProductProps = {
  chainProxyId: number | undefined
  onClick: (prediction: ProductPrediction) => void
}

function SelectProduct({ chainProxyId, onClick }: SelectProductProps) {
  const api = useApiService()

  const { data: predictions } = api.useGetProductPredictions({
    chainProxyId,
  })

  return (
    <div>
      {predictions?.map((p) => {
        return (
          <div
            key={p.product.id}
            onClick={() => onClick(p)}
            className="w-full bg-white rounded-lg shadow-md p-3 border border-gray-200 mb-3 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors"
          >
            Product: <span className="font-bold">{p?.product.name}</span>
          </div>
        )
      })}
    </div>
  )
}

export function MobileDealsCreate() {
  const api = useApiService()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Check for pre-selected chain from query param
  const preselectedChainId = searchParams.get('chain_id')
    ? Number(searchParams.get('chain_id'))
    : undefined

  const [selectedSearchResult, setSelectedSearchResult] = useState<
    ContactCompanySearchResult | DoorSearchResult | undefined
  >()
  const [selectedPrediction, setSelectedPrediction] = useState<
    ProductPrediction | undefined
  >()
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [showProductVariantModal, setShowProductVariantModal] = useState(false)
  const [selectedVariantName, setSelectedVariantName] = useState<string | null>(
    null
  )

  // Determine if the selected result is a door or company
  const isDoorSelected =
    selectedSearchResult && 'chain_proxy_id' in selectedSearchResult

  // For company selection: fetch chain data using item_id (chain ID)
  // Use preselectedChainId if no search result selected yet
  const chainId =
    selectedSearchResult && !isDoorSelected
      ? Number(selectedSearchResult.item_id)
      : preselectedChainId

  const { data: chainData } = api.useGetChain(chainId)

  // For door selection: fetch door proxy data using item_id (restaurant ID)
  const restaurantId =
    selectedSearchResult && isDoorSelected
      ? String(selectedSearchResult.item_id)
      : undefined

  const { data: doorProxy } = api.useGetPlaceV2(restaurantId)

  // Get chainProxyId from the appropriate source
  // - For company selection: from chainData
  // - For door selection: from doorProxy's chain_proxy.id
  const chainProxyId = isDoorSelected
    ? doorProxy?.chain_proxy?.id
    : chainData?.contact_company_id

  const {
    control,
    setValue,
    reset,
    handleSubmit,
    watch,
    formState: { isSubmitting, isValid },
  } = useForm<DealsFormValues>({
    resolver: zodResolver(dealsFormSchema),
    defaultValues: { deals: [], apply_to: 'entire_company' },
    mode: 'onChange',
  })

  // Mutation for creating deals
  const mutation = useMutation({
    mutationFn: async ({
      deals,
      applyTo,
      doorIds,
      companyId,
      doorProxyId,
      tableTypeSlug,
      unifiedTargetType,
      unifiedDoors,
    }: {
      deals: Partial<DealCreate>[]
      applyTo: string
      doorIds?: number[]
      companyId?: number
      doorProxyId?: number
      tableTypeSlug: TableTypeSlug
      // Routes the bulk create through the unified `/opportunities/` engine
      // (FIR-6997) so it no longer hits the legacy per-vertical bulk route.
      // Undefined for non-opportunity companies, which keep their legacy route.
      unifiedTargetType?: UnifiedTargetType
      unifiedDoors?: boolean
    }) => {
      if (isDoorSelected && doorProxyId) {
        const body = {
          deals,
          apply_to: applyTo,
          exclude_ids: [],
          include_ids: [doorProxyId],
          filter_params: {},
          unifiedTargetType,
          unifiedDoors,
        }
        return await api.commonCreateDeals(tableTypeSlug, body)
      } else if (companyId) {
        const body = {
          deals,
          apply_to: applyTo,
          door_ids: doorIds,
          include_ids: [companyId],
          exclude_ids: [],
          filter_params: {},
          unifiedTargetType,
          unifiedDoors,
        }
        return await api.commonCreateDeals(tableTypeSlug, body)
      } else {
        throw new Error('Missing companyId or doorProxyId')
      }
    },
    onError: () => {
      toast.error('Error creating deal')
    },
    onSuccess: (data) => {
      if (data.status === 202) {
        toast.info(
          data.data?.message ??
            'We are processing your request, you will be notified when it is done'
        )
      } else {
        toast.success('Deal created successfully')
        queryClient.invalidateQueries({ queryKey: [DEAL_TABLE_KEY] })
      }
      // Navigate to deals page
      navigate('/deals')
    },
  })

  // Determine company type slug from sub_category_slug or default to restaurants-bars
  const companyTypeSlug = useMemo<CompanyTypeSlug>(() => {
    if (
      selectedSearchResult &&
      'sub_category_slug' in selectedSearchResult &&
      selectedSearchResult.sub_category_slug
    ) {
      return selectedSearchResult.sub_category_slug as CompanyTypeSlug
    }
    // Default to restaurants-bars for FirstBite companies
    return 'restaurants-bars'
  }, [selectedSearchResult])

  // Calculate tableTypeSlug based on whether it's a door or company selection
  const tableTypeSlug = useMemo<TableTypeSlug>(() => {
    if (isDoorSelected) {
      // For doors, append '-doors' to the company type slug
      // COMPANY_TYPE_UPDATE
      switch (companyTypeSlug) {
        case 'restaurants-bars':
          return 'restaurants-bars-doors'
        case 'lodging-hospitality':
          return 'lodging-hospitality-doors'
        case 'convenience':
          return 'convenience-doors'
        default:
          // For other company types, use the company type slug as-is
          return companyTypeSlug
      }
    } else {
      // For companies, use the company type slug directly
      return companyTypeSlug
    }
  }, [isDoorSelected, companyTypeSlug])

  // Initialize form when prediction is selected
  useEffect(() => {
    if (!selectedPrediction) return

    const deals = [
      {
        create: true, // Auto-check the checkbox since user selected this prediction
        product: selectedPrediction.product.id!,
        product_variant: null,
        monthly_volume_override: null,
        monthly_revenue_override: null,
        close_date_override: null,
        origin_campaign_id: null,
        name: null,
        sales_stage: null,
        account_owner: null,
      },
    ]
    reset({ deals, apply_to: 'entire_company' })
  }, [selectedPrediction, reset])

  const handleClick = (
    result: ContactCompanySearchResult | DoorSearchResult
  ) => {
    setSelectedSearchResult(result)
  }

  const handlePredictionClick = (prediction: ProductPrediction) => {
    setSelectedPrediction(prediction)
  }

  const onSubmit = handleSubmit(async (values) => {
    // Validate we have the necessary IDs
    if (isDoorSelected && !doorProxy?.id) {
      toast.error('Door information not loaded')
      return
    }

    if (!isDoorSelected && !chainProxyId) {
      toast.error('Company ID not found')
      return
    }

    // Transform deals to match API format
    const dealsToCreate: Partial<DealCreate>[] = values.deals
      .filter((deal) => deal.create)
      .map((deal) => ({
        product_id: deal.product,
        product_variant_id: deal.product_variant ?? undefined,
        monthly_volume_override: deal.monthly_volume_override ?? undefined,
        monthly_revenue_override: deal.monthly_revenue_override ?? undefined,
        close_date_override: deal.close_date_override ?? undefined,
        sales_stage_id: deal.sales_stage ?? undefined,
        account_owner_id: deal.account_owner ?? undefined,
        origin_campaign_id: deal.origin_campaign_id ?? undefined,
        name: deal.name ?? undefined,
      }))

    if (dealsToCreate.length === 0) {
      toast.error('No deals selected to create')
      return
    }

    await mutation.mutateAsync({
      deals: dealsToCreate,
      applyTo: isDoorSelected ? 'select_locations' : values.apply_to,
      doorIds: values.door_ids,
      companyId: chainProxyId,
      doorProxyId: doorProxy?.id,
      tableTypeSlug,
      // Route opportunities through the unified engine; the `-doors` suffix on
      // `tableTypeSlug` is a legacy concept, so derive the target type from the
      // base `companyTypeSlug` and flag doors separately.
      unifiedTargetType: tryGetTargetType(companyTypeSlug),
      unifiedDoors: !!isDoorSelected,
    })
  })

  const applyTo = watch('apply_to')
  const doorIds = watch('door_ids')

  // Show location modal when user selects "select_locations"
  useEffect(() => {
    if (applyTo === 'select_locations' && selectedPrediction) {
      setShowLocationModal(true)
    }
  }, [applyTo, selectedPrediction])

  // If product variant modal is open, show it
  if (showProductVariantModal && selectedPrediction?.product?.id) {
    return (
      <MobileSelectProductVariantModal
        title="Create A Deal"
        productId={selectedPrediction.product.id}
        initialVariantId={watch('deals.0.product_variant') ?? null}
        onCancel={() => {
          setShowProductVariantModal(false)
        }}
        onSubmit={(variantId, variantName) => {
          setValue('deals.0.product_variant', variantId)
          setSelectedVariantName(variantName)
          setShowProductVariantModal(false)
        }}
      />
    )
  }

  // If location modal is open, show it
  if (showLocationModal && chainProxyId) {
    return (
      <MobileSelectLocationsModal
        title="Create A Deal"
        initialDoorIds={doorIds || []}
        chainProxyId={chainProxyId}
        onCancel={() => {
          setShowLocationModal(false)
        }}
        onSubmit={(selectedDoorIds: number[]) => {
          setValue('door_ids', selectedDoorIds)
          setShowLocationModal(false)
        }}
      />
    )
  }

  return (
    <MobileModal
      handleBack={() => {
        if (selectedPrediction) {
          setSelectedPrediction(undefined)
          setSelectedVariantName(null)
          reset({ deals: [], apply_to: 'entire_company' })
        } else if (selectedSearchResult) {
          setSelectedSearchResult(undefined)
        } else if (preselectedChainId) {
          // If chain was preselected, go back (likely to chain page)
          navigate(-1)
        } else {
          navigate(-1)
        }
      }}
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Create a Deal
          </div>
          {!selectedPrediction ? (
            <h1 className="text-2xl font-semibold text-gray-900">
              {!selectedSearchResult && !preselectedChainId
                ? 'Choose a company'
                : !selectedPrediction
                  ? 'Choose a product'
                  : 'Deal Details'}
            </h1>
          ) : (
            <h1 className="text-2xl text-gray-900">
              Product:{' '}
              <span className="font-semibold">
                {selectedPrediction.product.name}
              </span>
            </h1>
          )}
          {selectedSearchResult?.name && (
            <div className="text-xs text-fb-green-800 mt-2">
              <FbLink
                to={`${isDoorSelected ? (selectedSearchResult as DoorSearchResult).chain_proxy_id : selectedSearchResult.item_id}`}
              >
                {isDoorSelected
                  ? (selectedSearchResult as DoorSearchResult).chain_name
                  : selectedSearchResult.name}
              </FbLink>
            </div>
          )}
        </div>
      }
      footer={
        selectedPrediction ? (
          <div className="flex gap-4 p-2 justify-between">
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="secondary"
                onClick={() => {
                  setSelectedPrediction(undefined)
                  setSelectedVariantName(null)
                  reset({ deals: [], apply_to: 'entire_company' })
                }}
              >
                Cancel
              </FbButton>
            </div>
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="primary"
                onClick={onSubmit}
                disabled={!isValid || isSubmitting}
              >
                Create Deal
              </FbButton>
            </div>
          </div>
        ) : (
          <div></div>
        )
      }
    >
      {!selectedSearchResult && !preselectedChainId ? (
        <div className="w-full">
          <SearchPage hideBack onClick={handleClick} />
        </div>
      ) : !selectedPrediction ? (
        <div className="p-2">
          <div className="text-gray-500 text-xs mb-4">
            Deals allow you to track specific revenue opportunities throughout
            your sales pipeline. Each deal is done on a per product basis. For
            deals with unmatched companies, you can provide your own volume,
            revenue, and close date estimates.
          </div>
          <SelectProduct
            chainProxyId={chainProxyId}
            onClick={handlePredictionClick}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <MobileDealDetailsStep
            prediction={selectedPrediction!}
            control={control}
            companyTypeSlug={companyTypeSlug}
            companyDetails={isDoorSelected ? doorProxy?.chain_proxy : chainData}
            selectedVariantName={selectedVariantName}
            onSelectProductVariant={() => setShowProductVariantModal(true)}
            onClearProductVariant={() => {
              setValue('deals.0.product_variant', null)
              setSelectedVariantName(null)
            }}
          />

          {/* Apply this deal to section - only show for company selection, not door selection */}
          {!isDoorSelected && (
            <div className="px-4 pb-4">
              <div className="border border-gray-300 rounded-md p-3 flex flex-col gap-2">
                <div className="text-sm font-medium text-gray-700">
                  Apply this deal to:
                </div>

                <RadioIconSelect
                  name="apply_to"
                  control={control}
                  options={[
                    {
                      label: 'Entire Company',
                      key: 'entire_company',
                      icon: Building2,
                    },
                    {
                      label: doorIds?.length
                        ? `Select Locations (${doorIds.length})`
                        : 'Select Locations',
                      key: 'select_locations',
                      icon: MapPin,
                      onClick: () => setShowLocationModal(true),
                    },
                  ]}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </MobileModal>
  )
}
